import { useState, useCallback } from "react";
import { apiRequest } from "@/lib/queryClient";

interface TaxCalculationResult {
  taxAmount: number | null;
  success: boolean;
  error?: string;
}

interface UseTaxCalculationResult {
  calculateTax: (params: {
    customerZip: string;
    customerState: string;
    customerCountry: string;
    amount: number;
    servicesLaborTotal?: number;
    partsTotal?: number;
  }) => Promise<TaxCalculationResult>;
  isCalculating: boolean;
  lastResult: TaxCalculationResult | null;
}

export const useTaxCalculation = (): UseTaxCalculationResult => {
  const [isCalculating, setIsCalculating] = useState(false);
  const [lastResult, setLastResult] = useState<TaxCalculationResult | null>(
    null,
  );

  const calculateTax = useCallback(
    async (params: {
      customerZip: string;
      customerState: string;
      customerCountry: string;
      amount: number;
      servicesLaborTotal?: number;
      partsTotal?: number;
    }): Promise<TaxCalculationResult> => {
      setIsCalculating(true);

      try {
        const response = await apiRequest("POST", "/api/calculate-tax", {
          customer_zip: params.customerZip,
          customer_state: params.customerState,
          customer_country: params.customerCountry,
          amount: params.amount,
          services_labor_total: params.servicesLaborTotal,
          parts_total: params.partsTotal,
        });

        const result = await response.json();
        setLastResult(result);
        return result;
      } catch (error: any) {
        const errorResult: TaxCalculationResult = {
          taxAmount: null,
          success: false,
          error: `Unable to calculate tax. Please verify customer address (Customers page) and company address (Admin → Company Profile) are both complete. ${error.message || "Tax calculation failed"}`,
        };
        setLastResult(errorResult);
        return errorResult;
      } finally {
        setIsCalculating(false);
      }
    },
    [],
  );

  return {
    calculateTax,
    isCalculating,
    lastResult,
  };
};
